import React, { useRef, useState, useEffect } from 'react';
import { Phone, Move, ZoomIn, ZoomOut, RotateCcw, User, Check, Eye } from 'lucide-react';
import { getPositionedImageStyle } from '../../utils/imageStyle';

const DEFAULT_AGENT_AVATAR = 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=500&auto=format&fit=crop&q=80';

interface AgentCardLivePreviewProps {
  name: string;
  role: string;
  phone: string;
  email: string;
  image: string;
  category: 'sales' | 'rentals' | 'office';
  zoom: number;
  panX: number;
  panY: number;
  fit?: 'cover' | 'contain';
  onPanChange?: (newPanX: number, newPanY: number) => void;
  onZoomChange?: (newZoom: number) => void;
  onFitChange?: (newFit: 'cover' | 'contain') => void;
  onReset?: () => void;
}

export const AgentCardLivePreview: React.FC<AgentCardLivePreviewProps> = ({
  name,
  role,
  phone,
  email,
  image,
  category,
  zoom,
  panX,
  panY,
  fit = 'cover',
  onPanChange,
  onZoomChange,
  onFitChange,
  onReset
}) => {
  const photoContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ startX: number; startY: number; startPanX: number; startPanY: number }>({
    startX: 0,
    startY: 0,
    startPanX: 50,
    startPanY: 50
  });

  const hasCustomImage = Boolean(image && image.trim());
  const displayImage = hasCustomImage ? image.trim() : DEFAULT_AGENT_AVATAR;

  // Handle Mouse Drag on photo
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!onPanChange) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPanX: panX,
      startPanY: panY
    };
  };

  // Handle Touch Drag on photo
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!onPanChange || e.touches.length === 0) return;
    const touch = e.touches[0];
    setIsDragging(true);
    dragStartRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startPanX: panX,
      startPanY: panY
    };
  };

  // Handle Mouse Wheel Zoom on photo frame
  const handleWheel = (e: React.WheelEvent) => {
    if (!onZoomChange) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 5 : -5;
    const nextZoom = Math.max(50, Math.min(250, zoom + delta));
    onZoomChange(nextZoom);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!photoContainerRef.current || !onPanChange) return;
      const rect = photoContainerRef.current.getBoundingClientRect();
      const deltaX = ((e.clientX - dragStartRef.current.startX) / rect.width) * 100;
      const deltaY = ((e.clientY - dragStartRef.current.startY) / rect.height) * 100;

      // Inverted so dragging photo in a direction pulls that part into view
      const nextPanX = Math.round(Math.max(0, Math.min(100, dragStartRef.current.startPanX - deltaX)));
      const nextPanY = Math.round(Math.max(0, Math.min(100, dragStartRef.current.startPanY - deltaY)));
      onPanChange(nextPanX, nextPanY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!photoContainerRef.current || !onPanChange || e.touches.length === 0) return;
      const touch = e.touches[0];
      const rect = photoContainerRef.current.getBoundingClientRect();
      const deltaX = ((touch.clientX - dragStartRef.current.startX) / rect.width) * 100;
      const deltaY = ((touch.clientY - dragStartRef.current.startY) / rect.height) * 100;

      const nextPanX = Math.round(Math.max(0, Math.min(100, dragStartRef.current.startPanX - deltaX)));
      const nextPanY = Math.round(Math.max(0, Math.min(100, dragStartRef.current.startPanY - deltaY)));
      onPanChange(nextPanX, nextPanY);
    };

    const handleEnd = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, onPanChange]);

  const imageStyle = getPositionedImageStyle(zoom, panX, panY, fit, isDragging);

  return (
    <div className="flex flex-col items-center select-none w-full max-w-[320px]">
      {/* 1:1 Live Preview Header */}
      <div className="mb-2.5 text-center w-full">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider bg-[#0F1E32] text-[#C5A46E] shadow-sm border border-[#C5A46E]/40">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span>👁️ 1:1 Преглед в сайта</span>
        </div>
        <p className="text-[11px] text-slate-600 font-medium mt-1">
          Точно по този начин служителят ще се показва в секция „Екип“:
        </p>
      </div>

      {/* 1:1 Real Website Card Container (exact width and proportions as on the site) */}
      <div className="w-[270px] sm:w-[285px] bg-white rounded-2xl shadow-xl border-2 border-[#C5A46E]/50 overflow-hidden flex flex-col group transition-all relative">
        {/* Category Pill Tag */}
        <div className="absolute top-2.5 right-2.5 z-20">
          <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider shadow-sm text-white ${
            category === 'sales' ? 'bg-[#C5A46E]' : category === 'rentals' ? 'bg-[#1D608A]' : 'bg-slate-700'
          }`}>
            {category === 'sales' ? 'Продажби' : category === 'rentals' ? 'Наеми' : 'Офис'}
          </span>
        </div>

        {/* Real Photo Frame (h-64 = 256px matching website App.tsx) */}
        <div
          ref={photoContainerRef}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onWheel={handleWheel}
          className={`h-64 overflow-hidden relative bg-slate-100 shrink-0 flex items-center justify-center ${
            onPanChange ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : ''
          }`}
          title="Кликнете и влачете с мишката за наместване. Завъртете колелцето на мишката за зуум."
        >
          {/* Ambient blurred backdrop when photo is zoomed or panned or contain mode */}
          <img
            src={displayImage}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-35 pointer-events-none"
          />

          {/* Foreground sharp image with exact transform formula */}
          <img
            src={displayImage}
            alt={name || 'Служител'}
            referrerPolicy="no-referrer"
            className="w-full h-full relative z-10 pointer-events-none"
            style={imageStyle}
            onError={(e) => {
              (e.target as HTMLImageElement).src = DEFAULT_AGENT_AVATAR;
            }}
          />

          {/* Floating hint for dragging */}
          <div className="absolute bottom-2 inset-x-2 z-20 flex justify-center pointer-events-none">
            <span className="bg-black/75 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-md opacity-90 group-hover:opacity-100 transition">
              <Move className="w-3 h-3 text-[#C5A46E]" />
              Влачете с мишката за наместване
            </span>
          </div>

          {/* Active drag glow outline */}
          {isDragging && (
            <div className="absolute inset-0 border-2 border-[#C5A46E] pointer-events-none z-30 animate-pulse" />
          )}

          {/* Empty photo placeholder indicator */}
          {!hasCustomImage && (
            <div className="absolute top-2 left-2 z-20">
              <span className="bg-amber-500/90 text-white text-[9px] font-bold px-2 py-0.5 rounded shadow">
                Примерна снимка
              </span>
            </div>
          )}
        </div>

        {/* Real Website Card Details */}
        <div className="p-4 sm:p-5 flex flex-col flex-grow text-center bg-white">
          <h3 className="font-bold text-base sm:text-lg text-[#0F1E32] line-clamp-1">
            {name && name.trim() ? name : 'Име на служителя'}
          </h3>
          <p className="text-[11px] sm:text-xs text-[#C5A46E] font-semibold uppercase mt-0.5 line-clamp-1">
            {role && role.trim() ? role : 'Позиция в агенцията'}
          </p>

          <div className="mt-3.5 pt-3 border-t border-gray-150 flex flex-col gap-1.5 mt-auto">
            <div className="flex items-center justify-center gap-1.5 py-2 px-3 bg-slate-50 rounded-lg text-xs font-bold text-[#0F1E32] border border-gray-200">
              <Phone className="w-3.5 h-3.5 text-[#C5A46E] shrink-0" />
              <span className="truncate">{phone && phone.trim() ? phone : '+359 88 ...'}</span>
            </div>
            {email && email.trim() && (
              <span className="text-[10px] text-gray-400 font-mono italic truncate" title={email}>
                {email}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Interactive Controls directly beneath the card */}
      <div className="mt-3 bg-white p-3 rounded-xl border border-slate-200 shadow-sm w-full max-w-[285px] space-y-2.5">
        {/* Mode Toggle: Cover vs Contain */}
        {onFitChange && (
          <div>
            <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              <span>Режим на показване:</span>
              <span className="font-semibold text-[#0F1E32]">
                {fit === 'contain' ? 'Цяла снимка (Fit)' : 'Запълване (Cover)'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200">
              <button
                type="button"
                onClick={() => onFitChange('cover')}
                className={`py-1 rounded text-[10px] font-bold transition ${
                  fit === 'cover' ? 'bg-[#0F1E32] text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Запълва цялата рамка (портретен кадър)"
              >
                🖼️ Запълване
              </button>
              <button
                type="button"
                onClick={() => onFitChange('contain')}
                className={`py-1 rounded text-[10px] font-bold transition ${
                  fit === 'contain' ? 'bg-[#C5A46E] text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Показва 100% от снимката без никакво изрязване на главата или раменете"
              >
                🔲 Цяла снимка
              </button>
            </div>
          </div>
        )}

        {/* Directional D-pad for fine pixel adjustments */}
        {onPanChange && (
          <div>
            <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              <span>Фино преместване (стрелки):</span>
              <button
                type="button"
                onClick={() => onPanChange(50, 50)}
                className="text-[10px] font-bold text-[#C5A46E] hover:underline"
              >
                Центрирай
              </button>
            </div>
            <div className="flex justify-center">
              <div className="grid grid-cols-3 gap-1 w-32">
                <div />
                <button
                  type="button"
                  onClick={() => onPanChange(panX, Math.max(0, panY - 5))}
                  className="p-1.5 bg-slate-50 hover:bg-[#C5A46E] hover:text-white rounded border border-slate-200 text-xs font-bold transition flex items-center justify-center shadow-xs"
                  title="Нагоре (Фокус Лице / Глава)"
                >
                  ↑
                </button>
                <div />

                <button
                  type="button"
                  onClick={() => onPanChange(Math.max(0, panX - 5), panY)}
                  className="p-1.5 bg-slate-50 hover:bg-[#C5A46E] hover:text-white rounded border border-slate-200 text-xs font-bold transition flex items-center justify-center shadow-xs"
                  title="Наляво"
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={() => onPanChange(50, 50)}
                  className="p-1.5 bg-slate-100 hover:bg-[#0F1E32] hover:text-white rounded border border-slate-200 text-[10px] font-bold transition flex items-center justify-center shadow-xs text-slate-700"
                  title="Центрирай (50% / 50%)"
                >
                  ⊙
                </button>
                <button
                  type="button"
                  onClick={() => onPanChange(Math.min(100, panX + 5), panY)}
                  className="p-1.5 bg-slate-50 hover:bg-[#C5A46E] hover:text-white rounded border border-slate-200 text-xs font-bold transition flex items-center justify-center shadow-xs"
                  title="Надясно"
                >
                  →
                </button>

                <div />
                <button
                  type="button"
                  onClick={() => onPanChange(panX, Math.min(100, panY + 5))}
                  className="p-1.5 bg-slate-50 hover:bg-[#C5A46E] hover:text-white rounded border border-slate-200 text-xs font-bold transition flex items-center justify-center shadow-xs"
                  title="Надолу (Фокус Тяло)"
                >
                  ↓
                </button>
                <div />
              </div>
            </div>

            {/* Quick Focal Presets */}
            <div className="grid grid-cols-3 gap-1 mt-1.5">
              <button
                type="button"
                onClick={() => onPanChange(50, 15)}
                className={`py-1 px-1 rounded text-[9px] font-bold transition border ${
                  panY === 15 ? 'bg-[#C5A46E] text-white border-[#C5A46E]' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
                title="Позиционира рамката към горната част на снимката (лице и коса)"
              >
                👤 Лице (15%)
              </button>
              <button
                type="button"
                onClick={() => onPanChange(50, 50)}
                className={`py-1 px-1 rounded text-[9px] font-bold transition border ${
                  panY === 50 ? 'bg-[#0F1E32] text-white border-[#0F1E32]' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >
                👔 Център (50%)
              </button>
              <button
                type="button"
                onClick={() => onPanChange(50, 85)}
                className={`py-1 px-1 rounded text-[9px] font-bold transition border ${
                  panY === 85 ? 'bg-[#C5A46E] text-white border-[#C5A46E]' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >
                🧍 Тяло (85%)
              </button>
            </div>
          </div>
        )}

        {/* Quick Zoom Buttons */}
        {onZoomChange && (
          <div>
            <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              <span>Зуум / Мащаб:</span>
              <span className="font-mono font-bold text-[#C5A46E]">{zoom}%</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onZoomChange(Math.max(50, zoom - 5))}
                className="p-1 bg-slate-50 hover:bg-slate-200 rounded border border-slate-200 text-xs font-bold text-slate-700"
                title="Отдалечи (-5%)"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <div className="grid grid-cols-4 gap-1 flex-grow">
                {[75, 100, 125, 150].map((z) => (
                  <button
                    key={z}
                    type="button"
                    onClick={() => onZoomChange(z)}
                    className={`py-0.5 rounded text-[10px] font-bold transition border ${
                      zoom === z ? 'bg-[#0F1E32] text-white border-[#0F1E32]' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {z}%
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => onZoomChange(Math.min(250, zoom + 5))}
                className="p-1 bg-slate-50 hover:bg-slate-200 rounded border border-slate-200 text-xs font-bold text-slate-700"
                title="Приближи (+5%)"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Status Coordinate Bar */}
        <div className="flex items-center justify-between text-[10px] font-mono text-slate-500 pt-1.5 border-t border-slate-150">
          <span>X: <strong className="text-[#C5A46E]">{panX}%</strong></span>
          <span>Y: <strong className="text-[#C5A46E]">{panY}%</strong></span>
          <span>Зуум: <strong className="text-[#C5A46E]">{zoom}%</strong></span>
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              className="text-[#C5A46E] hover:text-[#0F1E32] flex items-center gap-0.5 text-[9px] font-sans font-bold"
              title="Върни настройките по подразбиране"
            >
              <RotateCcw className="w-2.5 h-2.5" />
              Ресет
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentCardLivePreview;
